// @givmo/app-admin — employer OCCASION PROGRAMS decode/build helpers (EP-BE8-web).
//
// Pure, side-effect-free helpers for the standing occasion rules a company authors ("every work
// anniversary → a $25 gift card") and the daily unattended sweep that fulfils them
// (`/admin/company/occasion-programs*`, EP-BE8). Kept as their own no-React module so the tolerant
// execution decode, the never-derive amount rule, the changed-fields-only PATCH body, and the status
// vocabulary are all unit-testable in isolation — the `gift-cards-decode.ts` / `-batch-decode.ts` split
// this feature already uses.
//
// ── THREE THINGS THIS MODULE EXISTS TO GET RIGHT ─────────────────────────────────────────────────
//
// 1. THE EXECUTION ROW IS UNTYPED IN THE GENERATED CLIENT, so it is hand-modelled HERE. Both paginated
//    occasion routes are annotated `-> dict` on the backend (the prevailing convention on this employer
//    surface), so FastAPI emits a bare `additionalProperties: true` object and never registers
//    `CompanyOccasionExecutionOut` — a regen generates no type for it. `CompanyOccasionProgramOut` is
//    rescued by the typed POST/PATCH/pause/resume routes and IS imported from the SDK; only the
//    execution shape (and its `held_reason` vocabulary) is declared locally, exactly as the gift-card
//    batches surface already does for its per-entry 422 rows. Every field is read defensively.
//
// 2. A PER-EXECUTION AMOUNT IS THE ROW'S OWN PIN OR IT IS NOTHING. The fence stores the price it was
//    CLAIMED at, and `PATCH` can re-price a program at any moment — so the program's current
//    denomination is not evidence of what a past occurrence was charged, and one occasion day can
//    legitimately hold two prices. A NULL pin (a fence claimed before the column existed) stays NULL on
//    the wire deliberately: the backend's read path does NOT fall back to the live program row even
//    though its CHARGE path does. Rendering the current rule there would fabricate history and let the
//    amount beside a HELD occasion drift under the admin between sweeps. Hence
//    {@link formatExecutionPinnedAmount} takes the EXECUTION ROW and nothing else — a program cannot
//    be passed to it, so the substitution is a type error rather than a code review.
//
// 3. PAUSE AND RESUME MEAN THE OPPOSITE OF WHAT THEY LOOK LIKE, so their copy is pinned here rather
//    than retyped per surface. Pausing freezes owed work (a paused program's HELD rows are never
//    retried — the debt is frozen, not cleared, and its alert goes quiet); resuming does NOT
//    back-charge the missed days (the sweep's lookback is floored at `resumed_at`).
//
// Money is cents-integer on this surface (`denomination_cents`), so it is formatted with the
// cent-exact helpers — never `formatUsd`, which rounds. Delivery-only per D-EP-17: nothing here reads
// or renders redemption state, and there is no "unclaimed" count to invent.

import {
  type CompanyGiftCardFundingSource,
  type CompanyOccasionExecutionStatus,
  type CompanyOccasionProgramCreate,
  type CompanyOccasionProgramOut,
  type CompanyOccasionProgramPreflightItemOut,
  type CompanyOccasionProgramUpdate,
  type CompanyOccasionType,
  type GivmoError,
  isGivmoError,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { formatCentsUsd } from '../dashboard/dashboard-decode';
import { GIFT_CARD_RANGE_LABEL, parseGiftCardAmount } from './gift-cards-decode';
import { PORTAL_WORDS, type EmployerModuleWords } from '../../lib/module-paths';

// ── Authoring bounds (mirrored from the backend schema + its DB CHECK) ────────────────────────────

/**
 * The recipient-message ceiling, mirroring the backend `_MESSAGE_MAX_LENGTH` (500) so an over-long
 * note is stopped at the textarea rather than by a 422.
 */
export const OCCASION_PROGRAM_MESSAGE_MAX = 500;

/**
 * The human range label for the denomination field. Deliberately the SAME string the attended
 * gift-card compose uses — a program mints the same instrument under the same statutory floors, so the
 * two surfaces must not quote different limits.
 */
export const OCCASION_DENOMINATION_RANGE_LABEL = GIFT_CARD_RANGE_LABEL;

/**
 * Parse the denomination input to CENTS, or `null` when it is not a valid in-range WHOLE-DOLLAR amount.
 * Every card a program mints carries its denomination as its face, and a card's face is whole dollars
 * (GC-FU27, D-GC-3 as amended 2026-11-02; the backend refuses a cents denomination with a 422). So this
 * is the attended compose's own strict parser (`parseGiftCardAmount`: digits only, [$15, $1,000]) times
 * 100 — the two surfaces mint the same instrument and must not accept different amounts. Blank /
 * malformed / fractional / out-of-range input keeps the form inert rather than sending a value the
 * backend would 422.
 */
export function parseDenominationCents(raw: string | null | undefined): number | null {
  const dollars = parseGiftCardAmount(raw);
  return dollars === null ? null : dollars * 100;
}

/**
 * The text that re-populates the denomination field when editing an existing rule. Whole dollars stay
 * bare ("25"); an odd-cents rule authored before GC-FU27 shows exactly what it is ("25.50") rather than
 * being silently re-priced by the edit form, and stays unsavable until the admin enters whole dollars.
 */
export function denominationCentsToInput(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

// ── Program list decode ──────────────────────────────────────────────────────────────────────────

/** Tolerant `{ data: [...] }` decode → the company's occasion programs (ACTIVE first). Never throws. */
export function extractOccasionPrograms(body: unknown): CompanyOccasionProgramOut[] {
  return extractList<CompanyOccasionProgramOut>(body);
}

// ── Execution row (HAND-MODELLED — see header note 1) ─────────────────────────────────────────────

/**
 * The held reasons this surface knows how to phrase. Hand-modelled because the enum has no OpenAPI
 * referrer (its only carrier is the untyped executions response).
 *
 * ★ The decoded row types `heldReason` as a plain `string`, NOT as this union, deliberately: the
 * backend can add a reason without a client release, and a decode that narrowed to the known set would
 * have to drop or mis-brand the new value. The union documents the vocabulary and drives the exhaustive
 * label switches; the tolerant `default` arm is what renders anything else.
 */
export type OccasionHeldReason =
  | 'VELOCITY_CAP_REACHED'
  | 'FUNDING_FAILED'
  | 'NO_PAYMENT_METHOD'
  | 'NO_RECEIPT_DESIGNEE'
  | 'EXECUTION_ERROR';

/** The known execution states — the generated union, re-exported so the filter chips can name them. */
export type OccasionExecutionStatusValue = CompanyOccasionExecutionStatus;

/**
 * ONE occasion execution — the fence row for a (program, employee, occurrence-year). Hand-modelled from
 * the backend `CompanyOccasionExecutionOut`; every field is tolerated-optional because this arrives
 * through an untyped response and a malformed row must still render rather than crash the table.
 *
 * DELIVERY-ONLY (D-EP-17): `gift_card_id` / `batch_id` join to the existing delivery report and there
 * is deliberately NO redemption state — whether the employee spent their gift is not the employer's
 * business, so no "unclaimed" count may be derived here or anywhere downstream.
 */
export interface OccasionExecutionRow {
  readonly id: string;
  readonly programId: string | null;
  readonly employeeUserId: string | null;
  readonly occurrenceYear: number | null;
  readonly occurrenceDate: string | null;
  /** Known values are {@link OccasionExecutionStatusValue}; typed open so an unknown one still renders. */
  readonly status: string | null;
  /** Known values are {@link OccasionHeldReason}; typed open so an unknown one still renders. */
  readonly heldReason: string | null;
  /**
   * The price this fence was CLAIMED at, in cents — echoed verbatim from the wire. `null` means no
   * price was ever pinned (a fence claimed before the column existed), and it MUST stay null: see
   * header note 2 and {@link formatExecutionPinnedAmount}.
   */
  readonly denominationCents: number | null;
  readonly giftCardId: string | null;
  readonly batchId: string | null;
  readonly attempts: number | null;
  readonly executedAt: string | null;
  readonly createdAt: string | null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Tolerantly decode the untyped executions response into renderable rows. Reads the bare array and the
 * `{data:[…]}` envelope, drops only entries with no usable `id` (nothing could key or address them),
 * and coerces every other field defensively so one malformed row degrades to blank cells instead of
 * throwing. Never throws (→ `[]`).
 */
export function extractOccasionExecutions(body: unknown): OccasionExecutionRow[] {
  const raw = extractList<unknown>(body);
  const out: OccasionExecutionRow[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const r = item as Record<string, unknown>;
    const id = str(r.id);
    if (id === null) continue;
    out.push({
      id,
      programId: str(r.program_id),
      employeeUserId: str(r.employee_user_id),
      occurrenceYear: num(r.occurrence_year),
      occurrenceDate: str(r.occurrence_date),
      status: str(r.status),
      heldReason: str(r.held_reason),
      denominationCents: num(r.denomination_cents),
      giftCardId: str(r.gift_card_id),
      batchId: str(r.batch_id),
      attempts: num(r.attempts),
      executedAt: str(r.executed_at),
      createdAt: str(r.created_at),
    });
  }
  return out;
}

// ── The never-derive amount rule (header note 2) ──────────────────────────────────────────────────

/**
 * The amount THIS execution was committed to, formatted cents-exact — or `null` when the row carries no
 * pin, in which case the caller OMITS the cell entirely.
 *
 * ★ It takes the execution row and nothing else, deliberately. The program's current
 * `denomination_cents` is not a substitute for a missing pin: a `PATCH` can re-price a program at any
 * moment, so the current rule says nothing about what a past occurrence was charged, and borrowing it
 * would let the amount beside a HELD occasion drift under the admin between sweeps — the exact
 * fabrication the pin exists to prevent. Because the signature admits no program, that substitution
 * cannot be written here without a type error.
 *
 * `null` renders as an omitted cell, never "$0" and never "—" with a number's weight: a price that was
 * never pinned is unknown, not zero.
 */
export function formatExecutionPinnedAmount(row: OccasionExecutionRow): string | null {
  return row.denominationCents === null ? null : formatCentsUsd(row.denominationCents);
}

// ── Display vocabulary (all tolerant of an unknown value — the enums are open on this surface) ────

/** Human label for the occasion a program fires on. Unknown → a neutral placeholder, never a throw. */
export function occasionTypeLabel(type: CompanyOccasionType | string | null | undefined): string {
  switch (type) {
    case 'BIRTHDAY':
      return 'Birthday';
    case 'WORK_ANNIVERSARY':
      return 'Work anniversary';
    case 'NEW_HIRE':
      return 'New hire';
    default:
      return 'Other occasion';
  }
}

/**
 * The rule restated in words for the authoring/list surfaces. Says WHEN it fires and never enumerates
 * a date — a program is a rule, and the wire deliberately carries no employee data at all.
 */
export function occasionTypeDescription(
  type: CompanyOccasionType | string | null | undefined,
): string {
  switch (type) {
    case 'BIRTHDAY':
      return 'On each employee’s birthday, from the dates your company has on file.';
    case 'WORK_ANNIVERSARY':
      return 'On each employee’s work anniversary, from their hire date.';
    case 'NEW_HIRE':
      return 'On each employee’s hire date, to welcome them.';
    default:
      return 'On a company-defined occasion.';
  }
}

/** Human label for the funding rail. Unknown → a neutral placeholder. */
export function occasionFundingLabel(
  funding: CompanyGiftCardFundingSource | string | null | undefined,
): string {
  switch (funding) {
    case 'company_card':
      return 'Company card';
    case 'company_wallet':
      return 'Company wallet';
    default:
      return '—';
  }
}

/** Human label for a program's lifecycle status. */
export function programStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'PAUSED':
      return 'Paused';
    default:
      return '—';
  }
}

export type OccasionBadgeTone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

/** Badge tone for a program status (unknown → neutral). */
export function programStatusTone(status: string | null | undefined): OccasionBadgeTone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PAUSED') return 'warning';
  return 'neutral';
}

/** Human label for an execution's state. Unknown values render as themselves-ish, never blank. */
export function executionStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'EXECUTED':
      return 'Sent';
    case 'HELD':
      return 'Held';
    case 'SKIPPED':
      return 'Skipped';
    default:
      return 'Unknown';
  }
}

/**
 * Badge tone for an execution's state. HELD is the only DANGER tone on this surface: a held row is a
 * gift the company owes and did not deliver, and it must not read like a benign in-progress state.
 */
export function executionStatusTone(status: string | null | undefined): OccasionBadgeTone {
  switch (status) {
    case 'EXECUTED':
      return 'success';
    case 'HELD':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * Human copy for a held reason — tolerant of an unknown value, which gets an honest generic line rather
 * than a blank or a guess.
 *
 * ★ `VELOCITY_CAP_REACHED` is a NORMAL state, not a bug: occasion spend shares the rolling 24-hour
 * employer window with attended sends, so a large bulk batch earlier in the day can legitimately leave
 * the afternoon occasion pass with no headroom. Its copy says so and points at the retry, because
 * "cap reached" alone reads like a defect the admin must fix.
 */
export function heldReasonLabel(reason: string | null | undefined): string {
  switch (reason) {
    case 'VELOCITY_CAP_REACHED':
      return 'Your company’s daily gift-card limit was already used — this retries automatically once the 24-hour window frees up. The limit is shared with gift cards you send yourself.';
    case 'FUNDING_FAILED':
      return 'The funding source could not cover this gift — the company card declined, or the wallet balance was short.';
    case 'NO_PAYMENT_METHOD':
      return 'This program has no usable funding source — add a saved company card, or set up the company wallet.';
    // ★ Rail-neutral first, deliberately. The enum name reads card-only, but the backend holds a
    // WALLET program for this reason too — the condition it actually tests is "no live company admin
    // at all" (neither the named designee nor an Owner), and a company with no admins is not one to
    // spend money for unattended on either rail. A wallet batch is receipt-less, so receipt-framed
    // copy would leave a wallet admin unable to recognise their own situation.
    case 'NO_RECEIPT_DESIGNEE':
      return 'Your company has no active admin for these gifts to be attributed to, so this was held rather than charged. For a card-funded gift, its tax receipt would also have nowhere to go. Add a company admin.';
    case 'EXECUTION_ERROR':
      return 'An unexpected problem stopped this gift. It retries on the next daily run.';
    default:
      return 'This gift is held and will be retried on the next daily run.';
  }
}

/** Short held-reason chip text for a table cell (the full sentence lives in the row detail). */
export function heldReasonShortLabel(reason: string | null | undefined): string {
  switch (reason) {
    case 'VELOCITY_CAP_REACHED':
      return 'Daily limit reached';
    case 'FUNDING_FAILED':
      return 'Funding failed';
    case 'NO_PAYMENT_METHOD':
      return 'No payment method';
    case 'NO_RECEIPT_DESIGNEE':
      return 'No company admin';
    case 'EXECUTION_ERROR':
      return 'Unexpected error';
    default:
      return 'Held';
  }
}

/**
 * What the program list's "Receipts to" column should say.
 *
 * ★ A WALLET-funded program issues NO receipt for its gifts, so it has no receipt recipient to name:
 * the company was already receipted when it DEPOSITED the funds, and re-attesting at spend time would
 * over-attest the same money. Naming a designee there would describe a document that never gets sent —
 * so a wallet program reads "—" even when a designee is stored on the row (the backend keeps one
 * because a program's funding is immutable but its admin team is not, and the value still gates the
 * no-live-admin hold).
 */
export function receiptRecipientLabel(
  program: CompanyOccasionProgramOut,
  words: Pick<EmployerModuleWords, 'receiptFallbackRecipient'> = PORTAL_WORDS,
): string {
  if (program.funding_source !== 'company_card') return '—';
  const designee = program.receipt_designee_user_id;
  return designee !== null && designee !== undefined
    ? 'A named admin'
    : words.receiptFallbackRecipient;
}

/** The execution-status filter chips, in the order the FilterBar renders them. */
export const EXECUTION_STATUS_FILTERS: readonly {
  label: string;
  value: CompanyOccasionExecutionStatus | null;
}[] = [
  { label: 'All', value: null },
  { label: 'Held', value: 'HELD' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Sent', value: 'EXECUTED' },
  { label: 'Skipped', value: 'SKIPPED' },
];

// ── Copy pinned here so every surface states the same non-obvious semantics ───────────────────────

/**
 * What pausing actually does. Pausing stops future occasions AND stops work on the debt already owed —
 * a paused program's held rows are never retried, so the debt sits FROZEN rather than clearing, and its
 * daily alert goes quiet. Saying only "held gifts stay visible" would read as "held gifts keep being
 * retried", which is the opposite of the truth.
 */
export const PAUSE_FREEZES_HELD_COPY =
  'Pausing stops future gifts and also stops work on gifts already owed — held gifts stay on the list but are frozen, not cleared, and are not retried until you resume.';

/**
 * What resuming does NOT do. The sweep's lookback is floored at the resume instant, so the days spent
 * paused are never funded retroactively — an unattended back-charge dressed up as recovery is exactly
 * what that floor exists to prevent.
 */
export const RESUME_NO_BACKCHARGE_COPY =
  'Resuming restarts future gifts and puts held gifts back in line. It does not go back and pay for occasions that passed while this program was paused.';

/**
 * The preflight verdict is ADVISORY. It is a pure read — no lock, no reservation — so it can only ever
 * say a funding source "may not cover" the horizon, never that it will. The real gate is the executor's
 * own balance check under the wallet lock at spend time, and an attended send can spend the shared
 * window in between.
 */
export const PREFLIGHT_ADVISORY_COPY =
  'This is an estimate, not a reservation — nothing is held aside. Balances and your company’s shared daily limit can change before the gifts go out.';

/** The one-active-program-per-occasion rule, restated where a 409 can land. */
export const DUPLICATE_PROGRAM_COPY =
  'Your company already has an active program for that occasion. Pause or edit the existing one instead — two live rules would send two gifts.';

// ── Error classification ─────────────────────────────────────────────────────────────────────────

/**
 * Whether a failed occasion read/write is the environment simply not having the feature on. Every
 * occasion route is behind a backend flag that 404s while off, and that flag flips INDEPENDENTLY of the
 * build-time switch that reveals this surface — gift cards may go live in an environment where occasion
 * programs are still dark. So a 404 here is "not available in this environment", a considered state,
 * and must never surface as an error toast or a broken panel.
 */
export function isOccasionUnavailableError(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * Whether a failed create/resume is the one-active-program-per-occasion conflict. The backend maps its
 * duplicate exception to HTTP **409** with readable server copy, which the UI surfaces verbatim.
 */
export function isOccasionConflictError(error: unknown): error is GivmoError {
  return isGivmoError(error) && error.status === 409;
}

// ── Create / update bodies ───────────────────────────────────────────────────────────────────────

/** The authoring draft the dialog holds, before it becomes a wire body. */
export interface OccasionProgramDraft {
  occasionType: CompanyOccasionType;
  denominationCents: number;
  funding: { kind: 'card'; cardId: string } | { kind: 'wallet' };
  recipientMessage: string;
  receiptDesigneeUserId: string | null;
}

/**
 * Build the `CompanyOccasionProgramCreate` body. A CARD program always carries its `card_id` — the
 * backend requires one at authoring because no admin is present when it charges, so "pick a card later"
 * has no later. Blank message/designee are OMITTED rather than sent as null.
 */
export function buildOccasionProgramCreateBody(
  draft: OccasionProgramDraft,
): CompanyOccasionProgramCreate {
  const body: CompanyOccasionProgramCreate = {
    occasion_type: draft.occasionType,
    denomination_cents: draft.denominationCents,
    funding_source: draft.funding.kind === 'card' ? 'company_card' : 'company_wallet',
  };
  if (draft.funding.kind === 'card') body.card_id = draft.funding.cardId;
  const message = draft.recipientMessage.trim();
  if (message.length > 0) body.recipient_message = message;
  if (draft.receiptDesigneeUserId !== null) {
    body.receipt_designee_user_id = draft.receiptDesigneeUserId;
  }
  return body;
}

/** The editable slice of a program — the only four fields `PATCH` accepts. */
export interface OccasionProgramEditDraft {
  denominationCents: number;
  recipientMessage: string;
  /** `null` = leave the program's card as it is (a card program's card can never be cleared). */
  cardId: string | null;
  receiptDesigneeUserId: string | null;
}

/**
 * Build the `PATCH` body carrying ONLY the fields that actually changed.
 *
 * ★ Absent-vs-null is load-bearing server-side: the backend reads `model_fields_set`, so an ABSENT key
 * leaves the stored value alone while an explicit `null` CLEARS it. Sending every field on every save
 * would therefore turn "I only changed the amount" into "…and clear the message and the designee".
 * A cleared message/designee is sent as an explicit `null`; an unchanged one is omitted entirely.
 *
 * The occasion TYPE and the funding SOURCE are absent by construction — `PATCH` does not accept them
 * (changing either is authoring a different program), so the edit surface offers no input for them.
 *
 * Returns `null` when nothing changed, so the caller can skip the request rather than send `{}`.
 */
export function buildOccasionProgramUpdateBody(
  original: CompanyOccasionProgramOut,
  draft: OccasionProgramEditDraft,
): CompanyOccasionProgramUpdate | null {
  const body: CompanyOccasionProgramUpdate = {};
  let changed = false;

  if (draft.denominationCents !== original.denomination_cents) {
    body.denomination_cents = draft.denominationCents;
    changed = true;
  }

  const nextMessage = draft.recipientMessage.trim();
  const priorMessage = (original.recipient_message ?? '').trim();
  if (nextMessage !== priorMessage) {
    body.recipient_message = nextMessage.length > 0 ? nextMessage : null;
    changed = true;
  }

  // A card program's card can be SWAPPED but never cleared (the backend refuses it — pausing is what
  // "stop this" means), so `null` here reads as "unchanged", never as "clear".
  if (draft.cardId !== null && draft.cardId !== original.card_id) {
    body.card_id = draft.cardId;
    changed = true;
  }

  const priorDesignee = original.receipt_designee_user_id ?? null;
  if (draft.receiptDesigneeUserId !== priorDesignee) {
    body.receipt_designee_user_id = draft.receiptDesigneeUserId;
    changed = true;
  }

  return changed ? body : null;
}

// ── Preflight display ────────────────────────────────────────────────────────────────────────────

/** A preflight item reduced to what the coverage meter renders. */
export interface PreflightCoverage {
  /** Cents of funding known to be available (wallet balance; a card's headroom is unknowable → null). */
  readonly availableCents: number | null;
  /** Cents the horizon's upcoming occasions will cost. */
  readonly upcomingCents: number;
  /** Meter fill, 0–100. 100 when the read cannot bound it (a card program). */
  readonly percent: number;
  readonly tone: 'success' | 'warning' | 'danger';
  /** Whether the advisory read thinks this program may fall short. */
  readonly atRisk: boolean;
}

/**
 * Reduce one preflight item to its meter geometry + tone. WALLET programs have a real available balance
 * to measure the horizon's cost against; CARD programs do not (a card's headroom is not knowable from a
 * read), so their meter reads full and the verdict rides the server's own `sufficient` /
 * `has_saved_card` instead of a fabricated ratio.
 *
 * The verdict is advisory in both cases — see {@link PREFLIGHT_ADVISORY_COPY}.
 */
export function preflightCoverage(item: CompanyOccasionProgramPreflightItemOut): PreflightCoverage {
  const upcomingCents = Number.isFinite(item.upcoming_total_cents) ? item.upcoming_total_cents : 0;
  const availableCents =
    typeof item.wallet_available_cents === 'number' && Number.isFinite(item.wallet_available_cents)
      ? item.wallet_available_cents
      : null;
  const atRisk = item.sufficient !== true;

  let percent = 100;
  if (availableCents !== null && upcomingCents > 0) {
    percent = Math.max(0, Math.min(100, Math.round((availableCents / upcomingCents) * 100)));
  }

  const tone = atRisk ? (percent < 50 ? 'danger' : 'warning') : 'success';
  return { availableCents, upcomingCents, percent, tone, atRisk };
}

/** Cent-exact label for a preflight cost/balance figure ("—" when unknown). */
export function formatOccasionCents(cents: number | null | undefined): string {
  return formatCentsUsd(typeof cents === 'number' && Number.isFinite(cents) ? cents : null);
}
